import React, { ReactElement, useCallback, useState } from 'react';
import classnames from 'classnames';

import Button, { BUTTON_WIDTHS } from '@buyside/components/presentation/Button/button';
import FilterCheckbox from '@buyside/components/presentation/Filters/FilterCheckbox/filterCheckbox';
import FilterChecklist from '@buyside/components/presentation/Filters/FilterChecklist/filterChecklist';
import FilterSelect from '@buyside/components/presentation/Filters/FilterSelect/filterSelect';
import FilterTextInput from '@buyside/components/presentation/Filters/FilterTextInput/filterTextInput';
import Modal from '@buyside/components/presentation/Modal/modal';
import SelectableDropdown from '@buyside/components/presentation/SelectableDropdown/selectableDropdown';

import { labelizedFilterListOptions } from '@buyside/components/presentation/Filters/filterUtil';

const {
  assetClasses,
  certifications,
  cities,
  geographies,
  ideaViews,
  investmentSituations,
  membersIndexJobTitles,
  sectors
} = labelizedFilterListOptions;

import styles from '../membersIndexPageStyles.scss';

export const renderMembersCheckbox = (disabled: boolean): ReactElement => (
  <FilterCheckbox
    text="Individual Accredited Investors"
    paramName="individual_accredited_investors"
    disabled={disabled}
    analyticsTag="member-filters-details-individual-accredited-investors"
  />
);

const MembersIndexFilters: React.FC<{
  disabled: boolean;
}> = ({ disabled }) => {
  const [isExpertiseModalOpen, setIsExpertiseModalOpen] = useState(false);

  const renderOpenExpertiseModalButton = useCallback(
    () => (
      <div>
        <Button
          className={classnames(styles.filterGroupModalButton, { [styles.disabled]: disabled })}
          onClick={() => setIsExpertiseModalOpen(true)}
          text="Knowledge Areas"
          width={BUTTON_WIDTHS.FULL}
        />
      </div>
    ),
    [disabled]
  );

  return (
    <>
      <FilterTextInput
        id="member-filter-details-name"
        placeholder="Name"
        paramName="name"
        disabled={disabled}
      />
      <FilterTextInput
        id="member-filter-details-employer"
        placeholder="Employer"
        paramName="employer"
        disabled={disabled}
      />
      <FilterTextInput
        id="member-filter-details-school"
        placeholder="School"
        paramName="school"
        disabled={disabled}
      />
      <Modal
        isOpen={isExpertiseModalOpen}
        onClose={() => setIsExpertiseModalOpen(false)}
        renderOpenModalButton={renderOpenExpertiseModalButton}
        size="lg"
        title="Expertise"
      >
        <div className={styles.filterGroupGrid}>
          <FilterChecklist
            title="Sectors"
            filterList={sectors.filter(({ label }) => label !== 'Unclassifiable')}
            paramName="sector[]"
          />
          <FilterChecklist title="Geographies" filterList={geographies} paramName="geographies[]" />
          <FilterChecklist
            title="Asset Classes"
            filterList={assetClasses}
            paramName="asset_classes[]"
          />
        </div>
      </Modal>
      <SelectableDropdown title="Investing Style" disabled={disabled}>
        <FilterChecklist paramName="investing_style" filterList={investmentSituations} />
      </SelectableDropdown>
      <SelectableDropdown title="City" overflowVisible disabled={disabled}>
        <FilterSelect paramName="location" options={cities} />
      </SelectableDropdown>
      <SelectableDropdown title="Title" disabled={disabled}>
        <FilterChecklist paramName="title" filterList={membersIndexJobTitles} />
      </SelectableDropdown>
      <SelectableDropdown title="Certifications" disabled={disabled}>
        <FilterChecklist paramName="certification_types[]" filterList={certifications} />
      </SelectableDropdown>
      <SelectableDropdown title="Idea Views" disabled={disabled}>
        <FilterChecklist paramName="idea_views" filterList={ideaViews} />
      </SelectableDropdown>
    </>
  );
};

export default MembersIndexFilters;
